import { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Delete } from 'assets/icons/Delete.svg';
import ConfirmationModal from 'components/ConfirmationModal';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useDeleteWorkspace from 'hooks/useDeleteWorkspace/useDeleteWorkspace';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { calculatePermissions } from 'hooks/useWorkspacePermissions/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';

import DeleteWorkspaceConfirmationMessage from './DeleteWorkspaceConfirmationMessage';
import ManagementActionSection from './ManagementActionSection';
import { getDirectSubWorkspaceCount } from './utils';
import WorkspaceSettingsModal from '../../WorkspaceSettingsModal';

const WorkspaceDeleteSetting = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const confirmationModal = useModal(ConfirmationModal);
  const { remove: removeWorkspaceSettingsModal } = useModal(WorkspaceSettingsModal);
  const { workspaceId, setWorkspaceId, workspacesState } = useWorkspacesContext({ requireWorkspaceId: true });
  const { deleteWorkspace } = useDeleteWorkspace();
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();

  const workspaceName = workspacesState.workspaces[workspaceId]?.name ?? '';
  const subWorkspaceCount = getDirectSubWorkspaceCount(workspacesState.workspaces, workspaceId);

  const handleDeleteWorkspace = async () => {
    if (!calculatePermissions(matrixClient, workspaceId).workspaceDelete) {
      pushNoPermissionToast();
      return;
    }

    try {
      await deleteWorkspace(workspaceId);
      setWorkspaceId(null);
      removeWorkspaceSettingsModal();
      pushToast({ variant: 'success', title: t('workspaces.workspace_deleted') });
    } catch (error) {
      logger.error('Error while deleting workspace', { workspaceId, error });

      if (error instanceof Error && error.message.includes('Insufficient power level')) return pushNoPermissionToast();

      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
    }
  };

  const displayModal = () =>
    confirmationModal.show({
      icon: Delete,
      message: (
        <DeleteWorkspaceConfirmationMessage workspaceName={workspaceName} subWorkspaceCount={subWorkspaceCount} />
      ),
      confirmButtonText: t('delete'),
      onConfirm: handleDeleteWorkspace,
    });

  return (
    <ManagementActionSection
      title={t('workspaces.delete_workspace')}
      description={t('workspaces.delete_workspace_desc')}
      buttonIcon={Delete}
      onClick={displayModal}
    />
  );
};

export default WorkspaceDeleteSetting;
